import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined, PlusOutlined, SaveOutlined, SearchOutlined } from '@ant-design/icons';
import ModalTextForm from "@app/components/modal/ModalTextForm";
import { Button, Col, DatePicker, Form, Input, InputNumber, Row, Space, Switch, Table, Tag, Tooltip } from 'antd';
import Column from 'antd/es/table/Column';
import dayjs from 'dayjs';
import { useEffect, useState } from "react";
import { useDispatch } from 'react-redux';
import { toast } from "react-toastify";
import { ContentHeader } from "../../components";
import { searchLength } from "../../constants";
import { dateCommon, validateMessages } from '../../helpers/common';
import { NhanSuKhac } from "../../models/nhansukhac/NhanSuKhac";
import { layDanhSachNhanSuKhac, luuNhanSuKhac, xoaNhanSuKhac } from "../../services/nhansukhac";
import { hideLoadingScreen, showLoadingScreen } from '../../store/reducers/loading';
import ChucVuSelectBox from "../utils/ChucVuSelectBox";
import NhanVienSelectBox from '../utils/NhanVienSelectBox';
import NhomNhanSuSelectBox from '../utils/NhomNhanSuSelectBox';

const QuanLyNhanSuKhac = () => {
    const [data, setData] = useState<Array<NhanSuKhac>>([]);
    const [tuKhoa, setTuKhoa] = useState<string>("");
    const [page, setPage] = useState(1);
    const [reloadPage, setReloadPage] = useState<boolean>(true);
    const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false);
    const [isLoadingBtn, setIsLoadingBtn] = useState<boolean>(false);
    const [showModalChiTiet, setShowModalChiTiet] = useState<boolean>(false);
    const [trangThai, setTrangThai] = useState<number>(1);
    const [idXoa, setIdXoa] = useState<number>(0);
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const setFormDetailModal = (value: NhanSuKhac | null) => {
        form.setFieldsValue({
            "id": value?.id || 0,
            "ma": value?.ma || "",
            "hoTen": value?.hoTen || "",
            "thuTu": value?.thuTu || 0,
            "idNhomNhanSu": value?.idNhomNhanSu || undefined,
            "idNhanVien": value?.idNhanVien || undefined,
            "idChucVu": value?.idChucVu != 0 ? value?.idChucVu : null,
            "trangThai": value?.trangThai || 0,
            "tuNgay": value?.tuNgay ? dayjs(dateCommon.formatDate(value?.tuNgay), "DD/MM/YYYY") : undefined,
            "denNgay": value?.denNgay ? dayjs(dateCommon.formatDate(value?.denNgay), "DD/MM/YYYY") : undefined,
        });
        setShowModalChiTiet(true);
    }

    const layDuLieu = async () => {
        try {
            setIsLoadingTable(true);
            let rs = await layDanhSachNhanSuKhac();
            setData(rs);
            setIsLoadingTable(false);
        } catch (error: any) {
            setIsLoadingTable(false);
        };
    }

    const onDelete = async () => {
        try {
            setIsLoadingBtn(true);
            await xoaNhanSuKhac(idXoa);
            toast.success('Xoá nhân sự thành công!');

            const totalRecords = data.length - 1; // Số lượng bản ghi sau khi xóa
            let newPage = page;

            if (newPage > Math.ceil(totalRecords / searchLength)) {
                // Nếu trang hiện tại lớn hơn số trang mới tối đa
                newPage = Math.ceil(totalRecords / searchLength);
            }

            setPage(newPage);
        }
        catch (err: any) {
            toast.error('Đã xảy ra lỗi, xoá không thành công!');
        }
        setIdXoa(0);
        setReloadPage(true);
        setIsLoadingBtn(false);

    }

    const onFinish = async (values: any) => {
        try {
            const { thuTu } = values;
            values.thuTu = thuTu == null ? 0 : thuTu;
            dispatch(showLoadingScreen());
            setIsLoadingBtn(true);
            await luuNhanSuKhac(values);
            setShowModalChiTiet(false);
            toast.success('Lưu thông tin nhân sự thành công!');
            setReloadPage(true);
        }
        catch (err: any) {
            toast.error(err.message || 'Đã xảy ra lỗi, lưu không thành công!');
        }
        dispatch(hideLoadingScreen());
        setIsLoadingBtn(false);
    };

    useEffect(() => {
        if (reloadPage) {
            layDuLieu();
            setReloadPage(false);
        }
    }, [reloadPage]);

    return (<section>
        <ContentHeader title="Quản lý Nhân sự không quản lý trực tiếp" />
        {idXoa > 0 && <ModalTextForm show={idXoa > 0}
            title="Xoá nhân sự"
            handleClose={() => setIdXoa(0)}
            handleSave={onDelete}>
            Xác nhận xóa nhân sự đang chọn
        </ModalTextForm>}
        {showModalChiTiet && <ModalTextForm title="Quản lý nhân sự "
            hideFooter
            show={showModalChiTiet}
            handleClose={() => { setFormDetailModal(null); setShowModalChiTiet(false) }}
        >
            <Form
                layout="vertical"
                validateMessages={validateMessages}
                onFinish={onFinish}
                form={form}
            >
                <Form.Item hidden name="id"><Input /></Form.Item>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Mã nhân sự"
                            name="ma"
                            rules={[{ required: true }]}
                        >
                            <Input placeholder="Mã nhân sự" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Tên nhân sự"
                            name="hoTen"
                            rules={[{ required: true, message: 'Vui lòng nhập tên nhân sự' }]}
                        >
                            <Input placeholder="Tên nhân sự" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="idChucVu" label="Chức vụ">
                            <ChucVuSelectBox allowClear={true} onChange={(e: any) => { form.setFieldValue('idChucVu', e.target.value); }} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Nhóm nhân sự"
                            name="idNhomNhanSu"
                            rules={[
                                {
                                    required: true,
                                }
                            ]}
                        >
                            <NhomNhanSuSelectBox onChange={(e: any) => { form.setFieldValue('idNhomNhanSu', e.target.value); }} />
                        </Form.Item>
                    </Col>  
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="idNhanVien" label="Nhân viên liên kết">
                            <NhanVienSelectBox allowClear={true} onChange={(e: any) => { form.setFieldValue('idNhanVien', e.target.value); }} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Từ ngày"
                            name="tuNgay"
                        >
                            <DatePicker changeOnBlur={true} disabledDate={(current: any) => {
                                const ngayKetThuc = form.getFieldsValue(['ngayKetThuc']);
                                return current && current > ngayKetThuc?.ngayKetThuc;
                            }} style={{ width: "100%" }} placeholder="Từ ngày" format={["DD/MM/YYYY", "DDMMYYYY"]} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Đến ngày"
                            name="denNgay"
                        >
                            <DatePicker
                                changeOnBlur={true}
                                disabledDate={(current: any) => {
                                    const ngayBatDau = form.getFieldsValue(['ngayBatDau']);
                                    return current && current < ngayBatDau?.ngayBatDau;
                                }} style={{ width: "100%" }} placeholder="Đến ngày" format={["DD/MM/YYYY", "DDMMYYYY"]} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Thứ tự"
                            name="thuTu" >
                            <InputNumber style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>
                    <Col className="gutter-row" span="12">
                        <Form.Item name="trangThai" label="Trạng thái" key="trangThai">
                            <div>
                                <Tooltip title={form.getFieldValue('trangThai') == 1 ? "Hoạt động" : "Không hoạt động"} placement="bottomLeft">
                                    <Switch
                                        checked={trangThai == 1 ? true : false}
                                        checkedChildren={<CheckOutlined rev="" />}
                                        unCheckedChildren={<CloseOutlined rev="" />}
                                        onChange={(e) => {
                                            setTrangThai(e ? 1 : 0);
                                            form.setFieldsValue({ trangThai: e ? 1 : 0 });
                                        }}
                                    />
                                </Tooltip>
                            </div>
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item>
                    <Button type="primary" loading={isLoadingBtn} className="float-right" htmlType="submit" icon=<SaveOutlined rev="" />>
                        Lưu
                    </Button>
                </Form.Item>
            </Form>
        </ModalTextForm>}
        <Row className="form-group" gutter={8}>
            <Col span={8}>
                <Input prefix={<SearchOutlined rev="" />} placeholder="Nhập mã, tên nhân sự, chức vụ để tìm kiếm" onChange={(e) => { setPage(1); setTuKhoa(e.target.value) }} />
            </Col>
            <Col span={16}>
                <Button
                    className="hrm-btn hrm-btn-warning float-right"
                    onClick={() => {
                        var data = new NhanSuKhac();
                        data.trangThai = 1;
                        data.thuTu = 1;
                        setTrangThai(1);
                        setFormDetailModal(data);
                    }}
                    icon={<PlusOutlined rev="" />}
                >
                    Thêm mới
                </Button>
            </Col>
        </Row>
        <Row gutter={8}>
            <Col span={24}>
                <div className="card card-primary">
                    <Row>
                        <Col span={24}>
                            <Table
                                loading={isLoadingTable}
                                locale={{ emptyText: "Không có dữ liệu" }}
                                dataSource={data.map((item: any, index) => { item.key = index; return item; }).filter((record: NhanSuKhac) =>
                                    (record?.tenChucVu?.toLowerCase().includes(tuKhoa.toLowerCase()) || record.ma.toLowerCase().includes(tuKhoa.toLowerCase()) || record.hoTen.toLowerCase().includes(tuKhoa.toLowerCase()) || record.ma.toLowerCase().includes(tuKhoa.toLowerCase()))
                                )}
                                rowClassName={(record, index) => {
                                    return (record?.id === idXoa || record?.id == form.getFieldValue("id")) ? "selected-row row-key-" : "";
                                }}
                                pagination={{
                                    current: page,
                                    onChange: (page, pageSize) => {
                                        setPage(page);
                                    },
                                }}
                            >
                                <Column
                                    title="STT"
                                    key="STT"
                                    render={(value, record, index) =>
                                    (
                                        <span key={"stt" + index}>
                                            {(page - 1) * searchLength + index + 1}
                                        </span>
                                    )
                                    }
                                />
                                <Column title="Mã nhân sự" dataIndex="ma" key="ma" />
                                <Column title="Tên nhân sự" dataIndex="hoTen" key="hoTen" />
                                <Column title="Chức vụ" dataIndex="tenChucVu" key="tenChucVu" />
                                <Column title="Từ ngày" key="tuNgay"
                                    render={(value, record, index) =>
                                    (
                                        <>
                                            {dateCommon.formatDate(value?.tuNgay)}
                                        </>
                                    )
                                    }
                                />
                                <Column title="Đến ngày" key="denNgay"
                                    render={(value, record, index) =>
                                    (
                                        <>
                                            {value?.denNgay ? dateCommon.formatDate(value.denNgay) : ''}
                                        </>
                                    )
                                    } />
                                <Column title="Thứ tự" dataIndex="thuTu" key="thuTu" />
                                <Column title="Trạng thái" key="trangThai"
                                    render={(value: NhanSuKhac) => (
                                        <Tag color={value.trangThai === 1 ? 'green' : 'red'}>
                                            {value.trangThai === 1 ? 'Đang hoạt động' : 'Không hoạt động'}
                                        </Tag>
                                    )} />
                                <Column
                                    title=""
                                    key="action"
                                    render={(value, record, index) =>
                                    (
                                        <Space>
                                            <Button type="primary" size={"small"} title="Sửa"
                                                icon=<EditOutlined rev="" />
                                                onClick={() => { setFormDetailModal(value); }}
                                            >
                                                Sửa
                                            </Button>
                                            <Button type="primary" danger size={"small"} title="Xóa"
                                                onClick={() => { setIdXoa(value.id) }}
                                                icon=<DeleteOutlined rev="" />
                                            >
                                                Xóa
                                            </Button>
                                        </Space>
                                    )
                                    }
                                />
                            </Table>
                        </Col>
                    </Row>
                </div>
            </Col>
        </Row>
    </section>
    );
}
export default QuanLyNhanSuKhac;